import { fromNode, TypedNode, ViewModel } from '@rehaje/core'
import { reactive } from '@rehaje/reactive'
import { isArray, toArr } from '@rehaje/shared'
import { createElement } from 'react'

import { SchemaModel } from './SchemaModel'
import { ValuableModel } from './ValuableModel'

import type { MutableRef, WidgetSpecific } from '../types'
import type { WidgetNode } from './WidgetNode'
import type { TRecord } from '@rehaje/shared'

export class WidgetModel extends ViewModel {
  /**
   * WidgetModel 特征标记
   */
  __r_is_widget_model = true

  @reactive()
  __r_props_version = 0

  __r_ref = {
    current: null
  }

  @fromNode()
  @reactive()
  hidden = false

  @fromNode()
  @reactive('deep')
  props?: TRecord

  get ref(): MutableRef | undefined {
    return this.getTypedNode<WidgetNode>().ref ? this.__r_ref : undefined
  }

  get schema() {
    const parent = this.getParent()
    if (parent instanceof SchemaModel) return parent
  }

  get valuableChildren(): ValuableModel[] {
    const children = []
    for (const child of this.getChildren()) {
      if (child instanceof ValuableModel) {
        children.push(child)
      } else if (child instanceof WidgetModel) {
        children.push(...(child.valuableChildren as ValuableModel[]))
      }
    }
    return children
  }

  constructor(node: WidgetNode) {
    super(node)
    this.addWatch((event) => {
      event.addEffect(() => {
        if (event.path[0] === 'props') {
          this.__r_props_version = NaN
        }
      })
    })
  }

  getComponentProps() {
    const schema = this.schema
    void this.__r_props_version
    return schema?.getComponentProps(this as WidgetSpecific<string, any>)
  }

  getRenderChildren(children?: any) {
    const selfChildren = this.renderChildren()
    if (isArray(children)) {
      if (children.length > 0) return children
      return selfChildren
    } else {
      return children ?? selfChildren
    }
  }

  override render(children?: any): any {
    if (this.hidden) return null
    const component = this.getPlugin().component
    const _children = this.getRenderChildren(children)
    return isArray(_children)
      ? createElement(component, this.getComponentProps(), ..._children)
      : createElement(component, this.getComponentProps(), _children)
  }

  renderChildren() {
    const node = this.getTypedNode<WidgetNode>()
    const children = toArr(node.children)
    if (children.length === 0) return null
    return children.map((child: TRecord) => {
      if (child instanceof TypedNode) return child.render()
      if (typeof child === 'object') {
        if (child instanceof HTMLElement) return null
        if (child.$$typeof !== undefined || child.nodeType !== undefined)
          return child
      } else {
        return child
      }
    })
  }
}
